import { useState } from "react";
import { ImportPanel } from "../components/ImportPanel";
import { copy } from "../copy";
import { accountConnected, connectionLine, metricsPlatformId, platformOf } from "../domain/accounts";
import { TrendChart } from "../components/TrendChart";
import { platformLabel } from "../domain/platforms";
import { snapshotsForPlatform, visiblePosts } from "../domain/selectors";
import { formatNumber } from "../lib/format";
import { newId, useApp } from "../state/store";
import type { AccountSnapshot, PlatformId, PostMetric } from "../types";

const METRICS = [
  { key: "followers", label: "粉丝" },
  { key: "posts", label: "发帖数" },
  { key: "views", label: "浏览" },
  { key: "likes", label: "点赞" },
  { key: "saves", label: "收藏" },
  { key: "comments", label: "评论" },
] as const;

type MetricKey = (typeof METRICS)[number]["key"];

export function Metrics() {
  const app = useApp();
  const spoken = platformOf(app.state.accounts ?? [], app.accountId);
  const platform: PlatformId = metricsPlatformId(spoken) ?? "xiaohongshu";
  const connected = accountConnected(spoken, app.state.snapshots, app.state.postMetrics);
  const [metric, setMetric] = useState<MetricKey>("followers");
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const rows = snapshotsForPlatform(app.state.snapshots, platform);
  const points = rows.flatMap((row) => {
    const value = row[metric];
    return value == null ? [] : [{ x: row.recordedDate, y: value }];
  });
  const posts = visiblePosts(app.state.postMetrics).filter((post) => post.platform === platform);

  return (
    <div className="stack">
      {connected ? (
        <p className="connection-line">{connectionLine(spoken, app.state.snapshots, app.state.postMetrics)}</p>
      ) : (
        <section className="panel stack connect-card">
          <h2>{copy.account.connectTitle}</h2>
          <p>{copy.account.offline}</p>
          <p className="muted">{copy.account.connectHow}</p>
          <button
            type="button"
            className="button primary"
            onClick={() => app.createTask({ type: "同步账号数据", platforms: [spoken], direction: "创作者中心", count: 1 })}
          >
            {copy.account.connectAction}
          </button>
          <p className="muted">也可以之后导入创作者中心导出的表格。</p>
          <ImportPanel />
        </section>
      )}
      {connected ? <section className="panel">
        <header className="panel-head">
          <h2>趋势</h2>
          <div className="row-actions">
            <span className="muted">{platformLabel(platform)}</span>
            <select value={metric} onChange={(event) => setMetric(event.target.value as MetricKey)} aria-label="指标">
              {METRICS.map((item) => (
                <option key={item.key} value={item.key}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </header>
        {rows.length === 0 ? <p className="muted">{copy.overview.unconnected}</p> : <TrendChart points={points} label={METRICS.find((item) => item.key === metric)?.label ?? "指标"} />}
      </section> : null}
      {connected ? <details className="manual-entry">
        <summary>导入表格</summary>
        <ImportPanel />
      </details> : null}
      {connected ? <details className="manual-entry">
        <summary>手动补一条</summary>
        <section className="split">
          <SnapshotForm platform={platform} />
          <PostForm platform={platform} />
        </section>
      </details> : null}
      {connected ? <section className="panel">
        <header className="panel-head">
          <h2>单条帖子</h2>
        </header>
        {posts.length === 0 ? (
          <p className="muted">还没有这个平台的单条数据。</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>日期</th>
                  <th>标题</th>
                  <th>浏览</th>
                  <th>点赞</th>
                  <th>收藏</th>
                  <th>评论</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id}>
                    <td>{post.recordedDate}</td>
                    <td>
                      {post.url ? (
                        <a href={post.url} target="_blank" rel="noreferrer">
                          {post.title || post.url}
                        </a>
                      ) : (
                        post.title || "未命名"
                      )}
                    </td>
                    <td>{formatNumber(post.views)}</td>
                    <td>{formatNumber(post.likes)}</td>
                    <td>{formatNumber(post.saves)}</td>
                    <td>{formatNumber(post.comments)}</td>
                    <td>
                      {pendingDelete === post.id ? (
                        <button type="button" className="button danger" onClick={() => app.deletePost(post.id)}>
                          确认删除
                        </button>
                      ) : (
                        <button type="button" className="button" onClick={() => setPendingDelete(post.id)}>
                          删除
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section> : null}
    </div>
  );
}

function SnapshotForm({ platform }: { platform: PlatformId }) {
  const app = useApp();
  const [date, setDate] = useState(todayValue());
  const [followers, setFollowers] = useState("");
  const [posts, setPosts] = useState("");
  const [views, setViews] = useState("");
  const [likes, setLikes] = useState("");
  const [saves, setSaves] = useState("");
  const [comments, setComments] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [duplicate, setDuplicate] = useState(false);

  return (
    <form
      className="panel stack"
      onSubmit={(event) => {
        event.preventDefault();
        if ([followers, posts, views, likes, saves, comments].some((value) => value.trim() && numberOrNull(value) == null)) {
          app.notify("数字栏里有无法识别的内容。", "danger");
          return;
        }
        const snapshot: AccountSnapshot = {
          id: newId(),
          platform,
          recordedDate: date,
          followers: numberOrNull(followers),
          posts: numberOrNull(posts),
          views: numberOrNull(views),
          likes: numberOrNull(likes),
          saves: numberOrNull(saves),
          comments: numberOrNull(comments),
          updatedAt: new Date().toISOString(),
          deletedAt: null,
        };
        const result = app.saveSnapshot(snapshot, overwrite);
        if (result === "duplicate") {
          setDuplicate(true);
          return;
        }
        setDuplicate(false);
        setOverwrite(false);
        app.notify(`已记下 ${platformLabel(platform)} ${date} 的账号数据。`);
      }}
    >
      <h2>录入账号当天数据</h2>
      <label>
        日期
        <input type="date" value={date} onChange={(event) => setDate(event.target.value)} required />
      </label>
      <div className="form-grid">
        <NumberField label="粉丝" value={followers} onChange={setFollowers} />
        <NumberField label="发帖数" value={posts} onChange={setPosts} />
        <NumberField label="浏览" value={views} onChange={setViews} />
        <NumberField label="点赞" value={likes} onChange={setLikes} />
        <NumberField label="收藏" value={saves} onChange={setSaves} />
        <NumberField label="评论" value={comments} onChange={setComments} />
      </div>
      {duplicate ? (
        <label className="check-line">
          <input type="checkbox" checked={overwrite} onChange={(event) => setOverwrite(event.target.checked)} />
          这一天已经有记录，勾选后再次保存会覆盖
        </label>
      ) : null}
      <button type="submit" className="button line">
        保存
      </button>
    </form>
  );
}

function PostForm({ platform }: { platform: PlatformId }) {
  const app = useApp();
  const [date, setDate] = useState(todayValue());
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [views, setViews] = useState("");
  const [likes, setLikes] = useState("");
  const [saves, setSaves] = useState("");
  const [comments, setComments] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [duplicate, setDuplicate] = useState(false);

  return (
    <form
      className="panel stack"
      onSubmit={(event) => {
        event.preventDefault();
        if ([views, likes, saves, comments].some((value) => value.trim() && numberOrNull(value) == null)) {
          app.notify("数字栏里有无法识别的内容。", "danger");
          return;
        }
        const post: PostMetric = {
          id: newId(),
          platform,
          topicId: null,
          title: title.trim(),
          url: url.trim(),
          recordedDate: date,
          views: numberOrNull(views),
          likes: numberOrNull(likes),
          saves: numberOrNull(saves),
          comments: numberOrNull(comments),
          updatedAt: new Date().toISOString(),
          deletedAt: null,
        };
        const result = app.savePost(post, overwrite);
        if (result === "duplicate") {
          setDuplicate(true);
          return;
        }
        setDuplicate(false);
        setTitle("");
        setUrl("");
        app.notify("已记下这条帖子。");
      }}
    >
      <h2>录入单条帖子</h2>
      <label>
        日期
        <input type="date" value={date} onChange={(event) => setDate(event.target.value)} required />
      </label>
      <label>
        标题
        <input value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label>
        链接
        <input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="选填，笔记链接" />
      </label>
      <div className="form-grid">
        <NumberField label="浏览" value={views} onChange={setViews} />
        <NumberField label="点赞" value={likes} onChange={setLikes} />
        <NumberField label="收藏" value={saves} onChange={setSaves} />
        <NumberField label="评论" value={comments} onChange={setComments} />
      </div>
      {duplicate ? (
        <label className="check-line">
          <input type="checkbox" checked={overwrite} onChange={(event) => setOverwrite(event.target.checked)} />
          同一条链接或标题已存在，勾选后覆盖
        </label>
      ) : null}
      <button type="submit" className="button line">
        保存帖子
      </button>
    </form>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label>
      {label}
      <input inputMode="numeric" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function numberOrNull(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const number = Number(trimmed.replace(/,/g, ""));
  return Number.isFinite(number) ? number : null;
}

function todayValue(): string {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}
